if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initMouseDrag();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // マウスでのドラッグ（クリック＆ドラッグ）で画像スライダーを横スクロールできるようにする。
      // タッチはネイティブのスクロール／スナップに任せるため pointerType === 'mouse' のみ対象。
      initMouseDrag() {
        const slider = this.elements.viewer && this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;
        // スライドが 2 枚未満なら横スワイプ不要
        if (slider.querySelectorAll('[id^="Slide-"]').length < 2) return;

        slider.classList.add('is-grabbable');

        const DRAG_THRESHOLD = 6;
        let isDown = false;
        let dragging = false;
        let startX = 0;
        let startScroll = 0;
        let moved = false;
        let activePointerId = null;

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          isDown = true;
          dragging = false;
          moved = false;
          startX = event.clientX;
          startScroll = slider.scrollLeft;
          activePointerId = event.pointerId;
          // setPointerCapture はドラッグ開始まで遅延する。
          // pointerdown で即キャプチャするとクリックがスライダーへ retarget され、
          // 画像拡大（modal-opener のボタン）のクリックが届かなくなるため。
        });

        slider.addEventListener('pointermove', (event) => {
          if (!isDown) return;
          const dx = event.clientX - startX;
          if (!dragging) {
            // 閾値未満はクリック候補として扱い、ドラッグを開始しない
            if (Math.abs(dx) <= DRAG_THRESHOLD) return;
            dragging = true;
            moved = true;
            slider.classList.add('is-dragging');
            try {
              slider.setPointerCapture(activePointerId);
            } catch (e) {
              // キャプチャ不可でもスクロール自体は継続する
            }
          }
          slider.scrollLeft = startScroll - dx;
          event.preventDefault();
        });

        const endDrag = () => {
          if (!isDown) return;
          isDown = false;
          if (dragging) {
            slider.classList.remove('is-dragging');
            if (activePointerId != null && slider.hasPointerCapture(activePointerId)) {
              slider.releasePointerCapture(activePointerId);
            }
          }
          dragging = false;
          activePointerId = null;
        };

        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);

        // ドラッグ直後のクリックは抑止して、画像ズーム等の誤発火を防ぐ。
        slider.addEventListener(
          'click',
          (event) => {
            if (moved) {
              event.preventDefault();
              event.stopPropagation();
              moved = false;
            }
          },
          true
        );
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
